// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useDevices } from '../context/DeviceContext';
import type { BaseMapItem, CheckinHistoryEntry } from '../types';
import { accountAPI, checkinAPI, deviceAPI, SummaryCheckpoint } from '../utils/apiClient';
import logger from '../utils/logger';

import { MusterSymbol } from './icons/AreaSymbols';

interface CheckinSummaryNodesProps {
  series: BaseMapItem[] | null;
  isChecker: boolean;
  isAdmin: boolean;
  /** Render in normal flow (parent positions it) instead of fixed top-centre. */
  inline?: boolean;
}

interface LiveEntry {
  id: string;
  title: string;
  checkinTime: Date | null;
}

/** Format a Date for <input type="datetime-local"> (local time, no TZ). */
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

const inputToIso = (v: string): string => (v ? new Date(v).toISOString() : '');

const formatTime = (t: Date | string | null | undefined): string => {
  if (!t) return '';
  const d = typeof t === 'string' ? new Date(t) : t;
  return isNaN(d.getTime()) ? String(t) : d.toLocaleString();
};

/**
 * Row of up to 4 live-count summary nodes for selected checkpoints.
 * Collapses when every selected checkpoint currently has 0 checked-in devices.
 */
export const CheckinSummaryNodes: React.FC<CheckinSummaryNodesProps> = ({
  series,
  isChecker,
  isAdmin,
  inline = false,
}) => {
  // Default: pin to the top centre of the viewport. inline drops the pinning so a
  // shared strip (e.g. alongside the device status boxes) can lay components out.
  const anchorStyle: React.CSSProperties = inline
    ? {}
    : {
        position: 'fixed',
        top: 'max(8px, 1vmin)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 40,
      };
  const { filteredDevices, fetchDevices } = useDevices();
  const [selection, setSelection] = useState<SummaryCheckpoint[]>([]);
  const [openDeveui, setOpenDeveui] = useState<string | null>(null);
  const [manualExpanded, setManualExpanded] = useState<boolean>(false);
  const [manualCollapsed, setManualCollapsed] = useState<boolean>(false);
  const canViewHistory = isChecker || isAdmin;
  const canManualCheckin = isChecker || isAdmin;

  useEffect(() => {
    accountAPI
      .getSummaryCheckpoints()
      .then((res) => setSelection(res.checkpoints || []))
      .catch((err) => {
        logger.error('Failed to load summary checkpoints', err);
      });
  }, []);

  const reload = useCallback(() => {
    accountAPI
      .getSummaryCheckpoints()
      .then((res) => setSelection(res.checkpoints || []))
      .catch(() => undefined);
  }, []);

  const countsByDeveui = useMemo(() => {
    const map: Record<string, number> = {};
    if (!series) return map;
    for (const item of series) {
      const cp = (item as BaseMapItem).checkinCheckpoint;
      if (!cp || !item.checkinTime) continue;
      // A checkpoint's own map item can carry a self-referential check-in
      // (id === checkinCheckpoint); a station is never checked into itself, so
      // exclude it or the node counts the station on top of its checked-in devices.
      if (String(item.id) === cp) continue;
      map[cp] = (map[cp] || 0) + 1;
    }
    return map;
  }, [series]);

  const liveByDeveui = useMemo(() => {
    const map: Record<string, LiveEntry[]> = {};
    if (!series) return map;
    for (const item of series) {
      const cp = (item as BaseMapItem).checkinCheckpoint;
      if (!cp || !item.checkinTime) continue;
      if (String(item.id) === cp) continue; // skip a station's self-check-in (see countsByDeveui)
      if (!map[cp]) map[cp] = [];
      const t = item.checkinTime;
      const d = t instanceof Date ? t : t ? new Date(t) : null;
      map[cp].push({
        id: String(item.id),
        title: item.title || String(item.id),
        checkinTime: d && !isNaN(d.getTime()) ? d : null,
      });
    }
    return map;
  }, [series]);

  const totalChecked = useMemo(
    () => selection.reduce((acc, c) => acc + (countsByDeveui[c.deveui] || 0), 0),
    [selection, countsByDeveui]
  );

  // Auto-expand when something is checked in; auto-collapse only when no modal is open
  useEffect(() => {
    if (totalChecked > 0) {
      setManualCollapsed(false);
    } else if (!openDeveui) {
      setManualExpanded(false);
    }
  }, [totalChecked, openDeveui]);

  const availableDevices = useMemo(
    () => filteredDevices.filter((d) => !d.checkpoint && !d.checkinTime),
    [filteredDevices]
  );

  const handleCheckin = useCallback(
    async (devId: string, checkpointDeveui: string) => {
      try {
        await deviceAPI.checkin(devId, checkpointDeveui);
        fetchDevices();
      } catch (err) {
        logger.error('Manual check-in failed', err);
      }
    },
    [fetchDevices]
  );

  const handleUncheck = useCallback(
    async (devId: string) => {
      try {
        await deviceAPI.uncheck(devId);
        fetchDevices();
      } catch (err) {
        logger.error('Uncheck failed', err);
      }
    },
    [fetchDevices]
  );

  const handleModalClose = useCallback(() => {
    setOpenDeveui(null);
    reload();
  }, [reload]);

  if (selection.length === 0) return null;

  const isExpanded = (totalChecked > 0 || manualExpanded || !!openDeveui) && !manualCollapsed;
  const nodeSize = 'max(48px, 7vmin)';
  const hasCheckins = totalChecked > 0;

  const modal = openDeveui ? (
    <CheckinSummaryModal
      checkpoint={selection.find((c) => c.deveui === openDeveui) || null}
      liveEntries={liveByDeveui[openDeveui] || []}
      availableDevices={availableDevices}
      canViewHistory={canViewHistory}
      canCheckin={canManualCheckin}
      onCheckin={handleCheckin}
      onUncheck={handleUncheck}
      onClose={handleModalClose}
    />
  ) : null;

  if (!isExpanded) {
    return (
      <>
        <button
          type="button"
          onClick={() => {
            setManualCollapsed(false);
            if (!hasCheckins) setManualExpanded(true);
          }}
          aria-label="Show checkpoint summary"
          style={{
            ...anchorStyle,
            background: hasCheckins ? '#50C878' : 'rgba(255,255,255,0.85)',
            border: 'none',
            borderRadius: '3px',
            padding: '2px 10px',
            cursor: 'pointer',
            color: hasCheckins ? 'white' : '#333',
            fontSize: 'max(11px, 1.4vmin)',
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {selection.length === 1 ? '▼ Checkpoint Summary' : '▼ Checkpoint Summaries'}
        </button>
        {modal}
      </>
    );
  }

  return (
    <>
      <div
        style={{
          ...anchorStyle,
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          pointerEvents: 'auto',
        }}
      >
        {selection.map((c) => {
          const count = countsByDeveui[c.deveui] || 0;
          return (
            <button
              key={c.deveui}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenDeveui(c.deveui);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                width: 'max(80px, 11vmin)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              aria-label={`${c.description || c.deveui}: ${count} checked in`}
            >
              <span
                style={{
                  position: 'relative',
                  width: nodeSize,
                  height: nodeSize,
                  borderRadius: '2px',
                  outline: '2px solid #1e7d1e',
                  outlineOffset: '3px',
                  display: 'block',
                }}
              >
                <MusterSymbol
                  style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
                />
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'max(16px, 2.6vmin)',
                    fontWeight: 700,
                    textShadow: '0 0 4px rgba(0,0,0,0.6)',
                  }}
                >
                  {count}
                </span>
              </span>
              <span
                style={{
                  fontSize: 'max(10px, 1.3vmin)',
                  color: '#000',
                  maxWidth: 'max(80px, 11vmin)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {c.description || c.deveui}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setManualCollapsed(true);
            setManualExpanded(false);
          }}
          aria-label="Collapse checkpoint summary"
          style={{
            alignSelf: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#555',
            fontSize: 'max(14px, 2vmin)',
            padding: '2px 4px',
            lineHeight: 1,
          }}
        >
          ▲
        </button>
      </div>

      {modal}
    </>
  );
};

const InlineUncheckButton: React.FC<{
  deviceId: string;
  onUncheck: (id: string) => Promise<void>;
}> = ({ deviceId, onUncheck }) => {
  const [pending, setPending] = useState(false);
  return (
    <button
      type="button"
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await onUncheck(deviceId);
        } finally {
          setPending(false);
        }
      }}
      style={{
        padding: '1px 6px',
        background: pending ? '#aaa' : '#e55',
        color: 'white',
        border: 'none',
        borderRadius: '3px',
        cursor: pending ? 'default' : 'pointer',
        fontSize: 'max(10px, 1.3vmin)',
      }}
    >
      Uncheck
    </button>
  );
};

const UncheckAllButton: React.FC<{
  liveEntries: LiveEntry[];
  onUncheck: (id: string) => Promise<void>;
}> = ({ liveEntries, onUncheck }) => {
  const [pending, setPending] = useState(false);
  return (
    <button
      type="button"
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await Promise.all(liveEntries.map((e) => onUncheck(e.id)));
        } finally {
          setPending(false);
        }
      }}
      style={{
        padding: '2px 8px',
        background: pending ? '#aaa' : '#e55',
        color: 'white',
        border: 'none',
        borderRadius: '3px',
        cursor: pending ? 'default' : 'pointer',
        fontSize: 'max(10px, 1.3vmin)',
      }}
    >
      Uncheck all
    </button>
  );
};

const InlineCheckinButton: React.FC<{
  deviceId: string;
  checkpointDeveui: string;
  onCheckin: (devId: string, cp: string) => Promise<void>;
}> = ({ deviceId, checkpointDeveui, onCheckin }) => {
  const [pending, setPending] = useState(false);
  return (
    <button
      type="button"
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await onCheckin(deviceId, checkpointDeveui);
        } finally {
          setPending(false);
        }
      }}
      style={{
        padding: '1px 6px',
        background: pending ? '#aaa' : '#50C878',
        color: 'white',
        border: 'none',
        borderRadius: '3px',
        cursor: pending ? 'default' : 'pointer',
        fontSize: 'max(10px, 1.3vmin)',
      }}
    >
      Check in
    </button>
  );
};

interface CheckinSummaryModalProps {
  checkpoint: SummaryCheckpoint | null;
  liveEntries: LiveEntry[];
  availableDevices: { id: string | number; title: string }[];
  canViewHistory: boolean;
  canCheckin: boolean;
  onCheckin: (deviceId: string, checkpointDeveui: string) => Promise<void>;
  onUncheck: (deviceId: string) => Promise<void>;
  onClose: () => void;
}

const CheckinSummaryModal: React.FC<CheckinSummaryModalProps> = ({
  checkpoint,
  liveEntries,
  availableDevices,
  canViewHistory,
  canCheckin,
  onCheckin,
  onUncheck,
  onClose,
}) => {
  const [showHistorical, setShowHistorical] = useState<boolean>(false);
  const [checkinOpen, setCheckinOpen] = useState<boolean>(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = React.useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onCloseRef.current(), 30000);
  }, []); // stable — captures latest onClose via ref, no deps needed

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const initialRange = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { from: toLocalInputValue(yesterday), to: toLocalInputValue(now) };
  }, []);
  const [from, setFrom] = useState<string>(initialRange.from);
  const [to, setTo] = useState<string>(initialRange.to);
  const [history, setHistory] = useState<CheckinHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const doSearch = useCallback(async () => {
    if (!checkpoint) return;
    setLoading(true);
    setError(null);
    try {
      const res = await checkinAPI.getHistory({
        from: inputToIso(from),
        to: inputToIso(to),
        checkpoint: checkpoint.deveui,
      });
      setHistory(res.history || []);
    } catch (err) {
      logger.error('Failed to fetch history', err);
      setError((err as Error).message || 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  }, [checkpoint, from, to]);

  useEffect(() => {
    if (showHistorical) doSearch();
  }, [showHistorical, doSearch]);

  if (!checkpoint) return null;

  // Render into <body> so the fixed overlay is positioned relative to the viewport.
  // The summary strip that mounts this modal lives inside a `transform`ed wrapper,
  // which would otherwise become the containing block for `position: fixed` and
  // trap the modal (and its backdrop click-to-close) inside that tiny strip.
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          resetTimer();
        }}
        style={{
          background: 'white',
          borderRadius: '1vmin',
          width: 'min(92vw, 640px)',
          maxHeight: '85vh',
          padding: '2vmin',
          display: 'flex',
          flexDirection: 'column',
          gap: '1vmin',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 'max(14px, 2vmin)', fontWeight: 600 }}>
            {checkpoint.description || checkpoint.deveui}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'max(16px, 2.4vmin)',
              cursor: 'pointer',
              padding: '0.5vmin 1vmin',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {canViewHistory && (
          <div style={{ display: 'flex', gap: '0.8vmin' }}>
            <button
              onClick={() => setShowHistorical(false)}
              style={{
                padding: '0.6vmin 1.2vmin',
                background: !showHistorical ? '#4CAF50' : '#eee',
                color: !showHistorical ? 'white' : '#333',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: 'pointer',
                fontSize: 'max(12px, 1.5vmin)',
              }}
            >
              Current
            </button>
            <button
              onClick={() => setShowHistorical(true)}
              style={{
                padding: '0.6vmin 1.2vmin',
                background: showHistorical ? '#4CAF50' : '#eee',
                color: showHistorical ? 'white' : '#333',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: 'pointer',
                fontSize: 'max(12px, 1.5vmin)',
              }}
            >
              Historical
            </button>
          </div>
        )}
        {showHistorical ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1vmin', overflow: 'auto' }}>
            <div style={{ display: 'flex', gap: '1vmin', flexWrap: 'wrap' }}>
              <label
                style={{ display: 'flex', flexDirection: 'column', fontSize: 'max(11px, 1.4vmin)' }}
              >
                From
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label
                style={{ display: 'flex', flexDirection: 'column', fontSize: 'max(11px, 1.4vmin)' }}
              >
                To
                <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <button onClick={doSearch} disabled={loading} style={{ alignSelf: 'end' }}>
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
            {error && <div style={{ color: '#c00' }}>{error}</div>}
            <div style={{ overflow: 'auto', maxHeight: '55vh' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'max(11px, 1.4vmin)',
                }}
              >
                <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.5vmin' }}>Time</th>
                    <th style={{ textAlign: 'left', padding: '0.5vmin' }}>Device</th>
                    <th style={{ textAlign: 'left', padding: '0.5vmin' }}>Cleared</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '0.5vmin' }}>{formatTime(h.timestamp)}</td>
                      <td style={{ padding: '0.5vmin' }}>
                        {h.device_description || h.device_name || h.device}
                      </td>
                      <td style={{ padding: '0.5vmin' }}>{h.cleared ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                  {!loading && history.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: '1vmin', color: '#888' }}>
                        No check-ins in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div
            style={{
              overflow: 'auto',
              maxHeight: '60vh',
              display: 'flex',
              flexDirection: 'column',
              gap: '1vmin',
            }}
          >
            {/* Checked-in list */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 'max(11px, 1.4vmin)', color: '#666' }}>
                {liveEntries.length} currently checked in
              </div>
              {canCheckin && liveEntries.length > 0 && (
                <UncheckAllButton liveEntries={liveEntries} onUncheck={onUncheck} />
              )}
            </div>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'max(11px, 1.4vmin)' }}
            >
              <thead style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5vmin' }}>Device</th>
                  <th style={{ textAlign: 'left', padding: '0.5vmin' }}>Checked in</th>
                  {canCheckin && <th style={{ padding: '0.5vmin' }} />}
                </tr>
              </thead>
              <tbody>
                {liveEntries.map((e) => (
                  <tr key={e.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '0.5vmin' }}>{e.title}</td>
                    <td style={{ padding: '0.5vmin' }}>{formatTime(e.checkinTime)}</td>
                    {canCheckin && (
                      <td style={{ padding: '0.5vmin' }}>
                        <InlineUncheckButton deviceId={e.id} onUncheck={onUncheck} />
                      </td>
                    )}
                  </tr>
                ))}
                {liveEntries.length === 0 && (
                  <tr>
                    <td colSpan={canCheckin ? 3 : 2} style={{ padding: '1vmin', color: '#888' }}>
                      No devices currently checked in.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Manual check-in list */}
            {canCheckin && availableDevices.length > 0 && checkpoint && (
              <>
                <button
                  type="button"
                  onClick={() => setCheckinOpen((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    borderTop: '1px solid #eee',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'max(11px, 1.4vmin)',
                    color: '#666',
                    fontWeight: 600,
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5vmin 0 0',
                  }}
                >
                  <span>{checkinOpen ? '▼' : '▶'}</span>
                  <span>Manual Check-in ({availableDevices.length})</span>
                </button>
                {checkinOpen && (
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 'max(11px, 1.4vmin)',
                    }}
                  >
                    <tbody>
                      {availableDevices.map((d) => (
                        <tr key={String(d.id)} style={{ borderTop: '1px solid #eee' }}>
                          <td style={{ padding: '0.5vmin' }}>{d.title}</td>
                          <td style={{ padding: '0.5vmin', textAlign: 'right' }}>
                            <InlineCheckinButton
                              deviceId={String(d.id)}
                              checkpointDeveui={checkpoint.deveui}
                              onCheckin={onCheckin}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
